import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import {
  IWorkOrder,
  ASSEMBLY_TYPE_LABELS,
  WO_STATUS_LABELS,
  WO_STATUS_COLORS,
  DEFECT_SEVERITY_LABELS,
  DEFECT_SEVERITY_COLORS,
  DEFECT_STATUS_LABELS,
  CHECKPOINT_TYPE_LABELS,
  OPERATOR_ROLE_LABELS,
} from '@/types/mes';
import { useDefects, useCheckpoints, useMaterials } from '@/hooks/useMES';

interface WorkOrderDialogProps {
  workOrder: IWorkOrder | null;
  isOpen: boolean;
  onClose: () => void;
  onStartWorkOrder?: (woId: string, operatorId: string) => void;
  onCompleteWorkOrder?: (woId: string, completedQty: number, scrappedQty: number) => void;
}

type TabType = 'details' | 'bom' | 'operators' | 'quality' | 'defects' | 'materials';

const WorkOrderDialog = ({
  workOrder,
  isOpen,
  onClose,
  onStartWorkOrder,
  onCompleteWorkOrder,
}: WorkOrderDialogProps) => {
  const { t } = useTranslation('mes');
  const [activeTab, setActiveTab] = useState<TabType>('details');

  // Fetch related data
  const { data: defects = [] } = useDefects(workOrder?.id || '');
  const { data: checkpoints = [] } = useCheckpoints(workOrder?.id || '');
  const { data: materials = [] } = useMaterials(workOrder?.id || '');

  if (!workOrder) return null;

  const tabs: { id: TabType; label: string }[] = [
    { id: 'details', label: t('tabs.details', 'Details') },
    { id: 'bom', label: t('tabs.bom', 'BOM') },
    { id: 'operators', label: t('tabs.operators', 'Operators') },
    { id: 'quality', label: t('tabs.quality', 'Quality') },
    { id: 'defects', label: t('tabs.defects', 'Defects') },
    { id: 'materials', label: t('tabs.materials', 'Materials') },
  ];

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-4 md:inset-10 bg-white dark:bg-gray-800 rounded-lg shadow-xl z-50 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {workOrder.wo_number}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {workOrder.bom_product_name} ({workOrder.bom_product_code})
                </p>
              </div>
              <div className="flex items-center gap-4">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${WO_STATUS_COLORS[workOrder.status]}`}
                >
                  {WO_STATUS_LABELS[workOrder.status]}
                </span>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="px-6 border-b border-gray-200 dark:border-gray-700">
              <nav className="flex gap-4 -mb-px">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    {tab.label}
                    {tab.id === 'defects' && defects.length > 0 && (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">
                        {defects.length}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Details Tab */}
              {activeTab === 'details' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {t('workOrder.info', 'Work Order Information')}
                    </h3>
                    <dl className="space-y-2">
                      <div className="flex justify-between">
                        <dt className="text-gray-500 dark:text-gray-400">{t('assemblyType', 'Assembly Type')}</dt>
                        <dd className="font-medium text-gray-900 dark:text-white">
                          {ASSEMBLY_TYPE_LABELS[workOrder.assembly_type]}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500 dark:text-gray-400">{t('level', 'Level')}</dt>
                        <dd className="font-medium text-gray-900 dark:text-white">{workOrder.level}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500 dark:text-gray-400">{t('sequence', 'Sequence')}</dt>
                        <dd className="font-medium text-gray-900 dark:text-white">{workOrder.sequence}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500 dark:text-gray-400">{t('lotNumber', 'Lot Number')}</dt>
                        <dd className="font-medium text-gray-900 dark:text-white">
                          {workOrder.lot_number || '-'}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {t('quantity.title', 'Quantity')}
                    </h3>
                    <dl className="space-y-2">
                      <div className="flex justify-between">
                        <dt className="text-gray-500 dark:text-gray-400">{t('quantity.planned', 'Planned')}</dt>
                        <dd className="font-medium text-gray-900 dark:text-white">{workOrder.planned_qty}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500 dark:text-gray-400">{t('quantity.completed', 'Completed')}</dt>
                        <dd className="font-medium text-green-600 dark:text-green-400">{workOrder.completed_qty}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500 dark:text-gray-400">{t('quantity.scrapped', 'Scrapped')}</dt>
                        <dd className="font-medium text-red-600 dark:text-red-400">{workOrder.scrapped_qty}</dd>
                      </div>
                    </dl>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {t('schedule.title', 'Schedule')}
                    </h3>
                    <dl className="space-y-2">
                      <div className="flex justify-between">
                        <dt className="text-gray-500 dark:text-gray-400">{t('schedule.plannedStart', 'Planned Start')}</dt>
                        <dd className="font-medium text-gray-900 dark:text-white">
                          {formatDate(workOrder.planned_start)}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500 dark:text-gray-400">{t('schedule.actualStart', 'Actual Start')}</dt>
                        <dd className="font-medium text-gray-900 dark:text-white">
                          {formatDate(workOrder.actual_start)}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500 dark:text-gray-400">{t('schedule.actualEnd', 'Actual End')}</dt>
                        <dd className="font-medium text-gray-900 dark:text-white">
                          {formatDate(workOrder.actual_end)}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {t('cycleTime.title', 'Cycle Time')}
                    </h3>
                    <dl className="space-y-2">
                      <div className="flex justify-between">
                        <dt className="text-gray-500 dark:text-gray-400">{t('cycleTime.planned', 'Planned')}</dt>
                        <dd className="font-medium text-gray-900 dark:text-white">
                          {workOrder.cycle_time_minutes} min
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500 dark:text-gray-400">{t('cycleTime.actual', 'Actual')}</dt>
                        <dd className="font-medium text-gray-900 dark:text-white">
                          {workOrder.actual_cycle_time_minutes} min
                        </dd>
                      </div>
                    </dl>
                  </div>

                  {/* Work Instructions */}
                  {workOrder.work_instructions && (
                    <div className="col-span-2 space-y-2">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {t('workInstructions', 'Work Instructions')}
                      </h3>
                      <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                        {workOrder.work_instructions}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* BOM Tab */}
              {activeTab === 'bom' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {t('bom.title', 'Bill of Materials')} ({workOrder.bom_items?.length || 0} items)
                  </h3>
                  {workOrder.bom_items && workOrder.bom_items.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              {t('bom.lineNo', 'Line')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              {t('bom.partCode', 'Part Code')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              {t('bom.description', 'Description')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              {t('bom.qtyPer', 'Qty Per')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              {t('bom.refDesignator', 'Ref Des')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {workOrder.bom_items.map((item, idx) => (
                            <tr key={idx} className={item.is_assembly ? 'bg-blue-50 dark:bg-blue-900/20' : ''}>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                {item.line_no}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                                {item.part_code}
                                {item.is_assembly && (
                                  <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded">
                                    Assembly
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                {item.description}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                {item.qty_per} {item.unit}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                {item.ref_designator || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">{t('bom.empty', 'No BOM items')}</p>
                  )}
                </div>
              )}

              {/* Operators Tab */}
              {activeTab === 'operators' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {t('operators.title', 'Assigned Operators')}
                  </h3>
                  {workOrder.operators && workOrder.operators.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {workOrder.operators.map((op, idx) => (
                        <div
                          key={idx}
                          className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                              <span className="text-lg font-medium text-blue-600 dark:text-blue-400">
                                {op.employee_name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {op.employee_name}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {OPERATOR_ROLE_LABELS[op.role]}
                              </p>
                            </div>
                          </div>
                          <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                            Assigned: {formatDate(op.assigned_at)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">{t('operators.empty', 'No operators assigned')}</p>
                  )}
                </div>
              )}

              {/* Quality Tab */}
              {activeTab === 'quality' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {t('quality.title', 'Quality Checkpoints')} ({checkpoints.length})
                  </h3>
                  {checkpoints.length > 0 ? (
                    <div className="space-y-3">
                      {checkpoints.map((cp) => (
                        <div
                          key={cp.id}
                          className={`p-4 rounded-lg border ${
                            cp.passed
                              ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                              : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {cp.checkpoint_name}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {CHECKPOINT_TYPE_LABELS[cp.checkpoint_type]}
                              </p>
                            </div>
                            <span
                              className={`px-3 py-1 rounded-full text-sm font-medium ${
                                cp.passed
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }`}
                            >
                              {cp.passed ? 'PASS' : 'FAIL'}
                            </span>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500 dark:text-gray-400">Spec: </span>
                              <span className="text-gray-900 dark:text-white">{cp.specification}</span>
                            </div>
                            <div>
                              <span className="text-gray-500 dark:text-gray-400">Actual: </span>
                              <span className="text-gray-900 dark:text-white">{cp.actual_value}</span>
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            Inspector: {cp.inspector_name} | {formatDate(cp.inspected_at)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">{t('quality.empty', 'No checkpoints recorded')}</p>
                  )}
                </div>
              )}

              {/* Defects Tab */}
              {activeTab === 'defects' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {t('defects.title', 'Defect Records')} ({defects.length})
                  </h3>
                  {defects.length > 0 ? (
                    <div className="space-y-3">
                      {defects.map((defect) => (
                        <div
                          key={defect.id}
                          className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900 dark:text-white">
                                {defect.defect_code}
                              </span>
                              <span className={`px-2 py-0.5 text-xs rounded-full ${DEFECT_SEVERITY_COLORS[defect.severity]}`}>
                                {DEFECT_SEVERITY_LABELS[defect.severity]}
                              </span>
                            </div>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              Qty: {defect.quantity}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300">{defect.description}</p>
                          {defect.root_cause && (
                            <div className="mt-2">
                              <span className="text-xs text-gray-500">Root Cause: </span>
                              <span className="text-sm text-gray-700 dark:text-gray-300">{defect.root_cause}</span>
                            </div>
                          )}
                          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                            <span>Discovered by: {defect.discovered_by}</span>
                            <span>{DEFECT_STATUS_LABELS[defect.status]}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">{t('defects.empty', 'No defects recorded')}</p>
                  )}
                </div>
              )}

              {/* Materials Tab */}
              {activeTab === 'materials' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {t('materials.title', 'Material Consumption')} ({materials.length})
                  </h3>
                  {materials.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Part Code
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Description
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                              Planned
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                              Consumed
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                              Scrapped
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {materials.map((mat) => (
                            <tr key={mat.id}>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                                {mat.part_code}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                {mat.part_description}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">
                                {mat.planned_qty}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-green-600 dark:text-green-400">
                                {mat.consumed_qty}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-red-600 dark:text-red-400">
                                {mat.scrapped_qty}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">{t('materials.empty', 'No materials recorded')}</p>
                  )}
                </div>
              )}
            </div>

            {/* Footer with Actions */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              {workOrder.status === 'ready' && onStartWorkOrder && (
                <button
                  onClick={() => onStartWorkOrder(workOrder.id, 'current-user')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('actions.start', 'Start Work Order')}
                </button>
              )}
              {workOrder.status === 'in_progress' && onCompleteWorkOrder && (
                <button
                  onClick={() => onCompleteWorkOrder(workOrder.id, workOrder.planned_qty, 0)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  {t('actions.complete', 'Complete Work Order')}
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {t('actions.close', 'Close')}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default WorkOrderDialog;
